/**
 * Runtime Plugin Loader
 *
 * Week 5 Decision Gate instrumentation (Phase 1)
 * - Initializes infrastructure plugins at app startup
 * - Captures Plugin_Initialized and Plugin_Error events
 * - Runtime-only (NOT build-time - see PLUGIN_HEALTH_MONITORING.md)
 *
 * Multi-Model Consensus (2025-12-09):
 * - CTO: CERTAIN confidence - PROCEED
 * - Code Reviewer: APPROVED WITH CRITICAL CHANGES
 * - Gemini 3 Pro: 9/10 confidence (identified 4 fixes)
 * - O3 Mini: 10/10 confidence
 * - Claude Sonnet 4.5: 8/10 confidence
 *
 * MANDATORY FIXES INCORPORATED:
 * 1. Fix #1 (Analytics API): Use `captureEvent` from '@/lib/analytics'
 * 2. Fix #2 (Plugin Validation): Validate exports before calling
 * 3. Fix #3 (Error Handling): Option B (Graceful Degradation) - UNANIMOUS
 * 4. Fix #4 (Metro Bundler): Static import map (preventative, Gemini recommendation)
 */

import { Platform } from 'react-native';
import { captureEvent } from '@/lib/analytics'; // Fix #1: Correct API
import {
  sanitizeStackTrace,
  PluginInitializedEvent,
  PluginErrorEvent,
  PluginPlatform,
} from '../../../packages/plugin-registry/src/types';

/**
 * Plugin import map
 *
 * Fix #4 (Metro Bundler): Static import map instead of template literals
 * Gemini 3 Pro (9/10 confidence) identified Metro bundler doesn't support
 * string-interpolated dynamic imports: `import(\`@dramadetector/${name}\`)`
 *
 * This static map ensures compatibility with Metro bundler's static analysis.
 *
 * Metro Resolution Fix: Using relative paths instead of workspace aliases
 * because plugin packages don't have proper "exports" fields in package.json.
 *
 * IMPORTANT: plugin-email is server-side only (requires 'postmark' Node.js package).
 * It's excluded from web builds to prevent bundler errors.
 */
const pluginImportMap = {
  'plugin-analytics': () => { const p = '../../../packages/plugins/plugin-analytics/src/index'; return import(p); },
  ...(Platform.OS !== 'web' ? {
    'plugin-email': () => { const p = '../../../packages/plugins/plugin-email/src/index'; return import(p); },
  } : {}),
} as const;

type PluginName = keyof typeof pluginImportMap;

/**
 * Get plugin version from module
 * Safely extracts version, defaults to '1.0.0' if not exported
 */
function getPluginVersion(pluginModule: any): string {
  return pluginModule.version || pluginModule.default?.version || '1.0.0';
}

/**
 * Initialize plugins at app startup (runtime context)
 *
 * Week 5 Decision Gate instrumentation (Phase 1):
 * - Plugin_Initialized: Success rate monitoring
 * - Plugin_Error: Debugging support for experiment validity
 *
 * Error Handling: Option B (Graceful Degradation) - UNANIMOUS (3/3 models)
 * - Logs errors to PostHog for debugging
 * - Does NOT re-throw (app continues without plugins)
 * - Rationale: Infrastructure plugins (analytics, email) are non-critical
 */
export async function initializePlugins(): Promise<void> {
  const pluginNames = Object.keys(pluginImportMap) as PluginName[];

  for (const pluginName of pluginNames) {
    const startTime = performance.now();

    try {
      // Fix #4: Static import map (Metro bundler compatibility)
      const importFn = pluginImportMap[pluginName];
      if (!importFn) continue;
      const pluginModule = await importFn();

      // Fix #2: Validate plugin exports before calling (HIGH priority)
      if (typeof pluginModule.initialize !== 'function') {
        throw new Error(
          `Plugin "${pluginName}" missing initialize() function. ` +
            `Expected: export function initialize() { ... }`
        );
      }

      // Initialize plugin
      await pluginModule.initialize();

      // ✅ Track successful initialization
      const event: PluginInitializedEvent = {
        plugin_name: pluginName,
        init_duration_ms: performance.now() - startTime,
        version: getPluginVersion(pluginModule),
        platform: Platform.OS as PluginPlatform, // 'web' | 'ios' | 'android'
      };

      // Fix #1: Use captureEvent (not analytics.capture)
      captureEvent('Plugin_Initialized', event as unknown as Record<string, unknown>);
    } catch (error) {
      // ✅ Track initialization errors (CRITICAL for Week 5 validity)
      const event: PluginErrorEvent = {
        plugin_name: pluginName,
        error_type: 'initialization',
        error_message: error instanceof Error ? error.message : String(error),
        stack_trace: sanitizeStackTrace(error instanceof Error ? error.stack : undefined),
      };

      // Fix #1: Use captureEvent (not analytics.capture)
      captureEvent('Plugin_Error', event as unknown as Record<string, unknown>);

      // Fix #3: Option B (Graceful Degradation) - UNANIMOUS
      // Log to console but DO NOT re-throw (app continues)
      console.error(`[Plugin] Failed to initialize "${pluginName}":`, error);
      // App continues startup without this plugin
    }
  }
}
